// src/services/claimsService.js
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../config/firebase';

export const ROLES = {
  SUPER_ADMIN: 'super-admin',
  ADMIN: 'admin',
  USER: 'user'
};

export const BOOK_ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  EDITOR: 'editor',
  CONTRIBUTOR: 'contributor',
  VIEWER: 'viewer'
};

// Role hierarchy for permission checks
const ROLE_HIERARCHY = {
  'super-admin': 100,
  'admin': 80,
  'user': 10
};

const BOOK_ROLE_HIERARCHY = {
  'owner': 100,
  'admin': 80,
  'editor': 60,
  'contributor': 40,
  'viewer': 20
};

class ClaimsService {
  // Get current user's claims from token
  async getCurrentClaims() {
    const user = auth.currentUser;
    if (!user) return null;
    
    const tokenResult = await user.getIdTokenResult(true); // Force refresh
    return tokenResult.claims;
  }
  
  // Get user's role
  async getUserRole() {
    const claims = await this.getCurrentClaims();
    return claims?.role || 'user';
  }
  
  // Get user's book role
  async getBookRole(bookId) {
    const claims = await this.getCurrentClaims();
    return claims?.bookRoles?.[bookId] || null;
  }
  
  // Check if user has minimum role
  async hasMinRole(requiredRole) {
    const userRole = await this.getUserRole();
    return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
  }
  
  // Check if user has minimum book role
  async hasMinBookRole(bookId, requiredRole) {
    const bookRole = await this.getBookRole(bookId);
    if (!bookRole) return false;
    return BOOK_ROLE_HIERARCHY[bookRole] >= BOOK_ROLE_HIERARCHY[requiredRole];
  }
  
  // Check specific permission
  async canManageBook(bookId) {
    const claims = await this.getCurrentClaims();
    const bookRole = claims?.bookRoles?.[bookId];
    return ['owner', 'admin'].includes(bookRole) || claims?.role === 'super-admin';
  }
  
  async canEditBook(bookId) {
    const claims = await this.getCurrentClaims();
    const bookRole = claims?.bookRoles?.[bookId];
    return ['owner', 'admin', 'editor'].includes(bookRole) || claims?.role === 'super-admin';
  }
  
  async canContributeToBook(bookId) {
    const claims = await this.getCurrentClaims();
    const bookRole = claims?.bookRoles?.[bookId];
    return ['owner', 'admin', 'editor', 'contributor'].includes(bookRole);
  }
  
  // Set user claims (admin only)
  async setUserClaims(uid, claims) {
    const setClaimsFn = httpsCallable(functions, 'setUserClaims');
    const result = await setClaimsFn({ uid, claims });
    return result.data;
  }
  
  // Add book role
  async addBookRole(targetUid, bookId, role) {
    const addRoleFn = httpsCallable(functions, 'addBookRole');
    const result = await addRoleFn({ targetUid, bookId, role });
    
    // Force token refresh for current user if they're the target
    if (auth.currentUser?.uid === targetUid) {
      await auth.currentUser.getIdToken(true);
    }
    
    return result.data;
  }
  
  // Remove book role
  async removeBookRole(targetUid, bookId) {
    const removeRoleFn = httpsCallable(functions, 'removeBookRole');
    const result = await removeRoleFn({ targetUid, bookId });
    return result.data;
  }
  
  // Force refresh token to get updated claims
  async refreshClaims() {
    const user = auth.currentUser;
    if (!user) return null;
    
    await user.getIdToken(true);
    return this.getCurrentClaims();
  }
}

export const claimsService = new ClaimsService();
export default claimsService;